import { RootState } from '@redux/store';
import { createSelector } from '@reduxjs/toolkit';
import { HeightUnits } from '@utils/unit.utils';

import { DisplayModeEnum } from '@/types/authent/login.types';
import { CoordinatesUnitEnum } from '@/types/commons/commons.types';
import { ContextEnum } from '@/types/config/screenConfiguration.types';
import { UserSettings } from '@/types/config/settings.types';
import { MapFilters } from '@/types/filters.types';

import { SettingsState } from './settings.reducer';

export function selectSettingsState(state: RootState): SettingsState {
  return state.settings;
}

export const selectUserSettings = createSelector(
  selectSettingsState,
  (state: SettingsState): UserSettings => state.userSettings,
);

export const selectLanguage = createSelector(selectUserSettings, (state: UserSettings): string => state.language);

export const selectDisplayMode = createSelector(
  selectUserSettings,
  (state: UserSettings): DisplayModeEnum => state.displayMode,
);

export const selectContext = createSelector(selectUserSettings, (state: UserSettings): ContextEnum => state.context);

export const selectCoordinatesUnit = createSelector(
  selectUserSettings,
  (state: UserSettings): CoordinatesUnitEnum => state.coordinatesUnit,
);

export const selectHeightUnit = createSelector(
  selectUserSettings,
  (state: UserSettings): HeightUnits => state.heightUnit,
);

export const selectAltitudeUnit = createSelector(
  selectUserSettings,
  (state: UserSettings): HeightUnits => state.altitudeUnit,
);

export const selectCurrentBaseLayerId = createSelector(
  selectUserSettings,
  (state: UserSettings): number => state.baseLayer,
);

export const selectOverlaysKey = createSelector(selectUserSettings, (state: UserSettings) => state.overlays);

export const selectAxisInvertedY = createSelector(
  selectUserSettings,
  (state: UserSettings): boolean => state.axisInvertedY,
);

export const selectLinearJoystickCoefficient = createSelector(
  selectUserSettings,
  (state: UserSettings): number => state.linearJoystickCoefficient,
);

export const selectZoomJoystickCameraSensitivity = createSelector(
  selectUserSettings,
  (state: UserSettings): number => state.zoomJoystickCameraSensitivity,
);

export const selectAutomatedLad = createSelector(
  selectUserSettings,
  (state: UserSettings): boolean => state.automatedLad,
);

// Use selectActualMapFilters to filter features on the map,
// this selector return the last updated value from getUserSettings
export const selectMapFilters = createSelector(
  selectUserSettings,
  (state: UserSettings): MapFilters => state.mapFilters,
);

export const selectLocalMapFilters = createSelector(
  selectSettingsState,
  (state: SettingsState): Partial<MapFilters> => state.localMapFilters,
);

export const selectActualMapFilters = createSelector(
  [selectMapFilters, selectLocalMapFilters],
  (mapFilters: MapFilters, localMapFilters: Partial<MapFilters>): MapFilters => ({ ...mapFilters, ...localMapFilters }),
);

export const selectLocalMapFilterByKey = createSelector(
  [selectActualMapFilters, (state, filterKey: keyof MapFilters) => filterKey],
  (mapFilters, filterKey): boolean => {
    return mapFilters[filterKey];
  },
);

export const selectHiddenFilterKeys = createSelector([selectActualMapFilters], (mapFilters) =>
  Object.keys(mapFilters).filter((filterKey) => !mapFilters[filterKey as keyof MapFilters]),
);

export const selectSpeedVectorLength = createSelector(
  selectUserSettings,
  (state: UserSettings): number => state.speedVectorLength,
);

export const selectHeightSuffix = createSelector(
  selectUserSettings,
  (state: UserSettings): boolean => state.heightSuffix,
);
